/**
 * Achievement Images Mapping
 * 
 * Modern image URLs for achievement badges
 * Images can be hosted on CDN, Firebase Storage, or local assets
 */

export type AchievementImageSource = {
  unlocked: string; // Image URL for unlocked achievement
  locked?: string; // Optional image URL for locked achievement (defaults to unlocked with opacity)
};

/**
 * Achievement image mappings
 * Using placeholder URLs - replace with your actual image hosting service
 * Options:
 * 1. Firebase Storage URLs
 * 2. CDN URLs (Cloudinary, Imgix, etc.)
 * 3. Local require() paths for bundled images
 */
export const ACHIEVEMENT_IMAGES: Record<string, AchievementImageSource> = {
  // Explorer Achievements
  first_steps: {
    unlocked: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=200&h=200&fit=crop&auto=format',
    // Using Unsplash as placeholder - replace with actual achievement images
  },
  country_collector_5: {
    unlocked: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=200&h=200&fit=crop&auto=format',
  },
  country_collector_10: {
    unlocked: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200&h=200&fit=crop&auto=format',
  },
  country_collector_25: {
    unlocked: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=200&h=200&fit=crop&auto=format',
  },
  country_collector_50: {
    unlocked: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200&h=200&fit=crop&auto=format',
  },
  country_collector_100: {
    unlocked: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=200&h=200&fit=crop&auto=format',
  },
  city_explorer_10: {
    unlocked: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=200&h=200&fit=crop&auto=format',
  },
  city_explorer_25: {
    unlocked: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=200&h=200&fit=crop&auto=format',
  },
  city_explorer_50: {
    unlocked: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=200&h=200&fit=crop&auto=format',
  },
  city_explorer_100: {
    unlocked: 'https://images.unsplash.com/photo-1496564203459-88bb61ec46bb?w=200&h=200&fit=crop&auto=format',
  },
  continent_hopper: {
    unlocked: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=200&h=200&fit=crop&auto=format',
  },
  weekend_warrior: {
    unlocked: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=200&h=200&fit=crop&auto=format',
  },
  month_master_3: {
    unlocked: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=200&h=200&fit=crop&auto=format',
  },
  month_master_6: {
    unlocked: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200&h=200&fit=crop&auto=format',
  },
  month_master_12: {
    unlocked: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=200&h=200&fit=crop&auto=format',
  },
  // Landmark Achievements
  landmark_hunter_10: {
    unlocked: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=200&h=200&fit=crop&auto=format',
  },
  landmark_hunter_25: {
    unlocked: 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=200&h=200&fit=crop&auto=format',
  },
  landmark_hunter_50: {
    unlocked: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200&h=200&fit=crop&auto=format',
  },
  world_wonder_seeker: {
    unlocked: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200&h=200&fit=crop&auto=format',
  },
  icon_collector: {
    unlocked: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=200&h=200&fit=crop&auto=format',
  },
  // Travel Style Achievements
  solo_adventurer: {
    unlocked: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=200&h=200&fit=crop&auto=format',
  },
  family_explorer: {
    unlocked: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=200&h=200&fit=crop&auto=format',
  },
  business_traveler: {
    unlocked: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=200&h=200&fit=crop&auto=format',
  },
  budget_master: {
    unlocked: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=200&h=200&fit=crop&auto=format',
  },
  planning_pro: {
    unlocked: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=200&h=200&fit=crop&auto=format',
  },
  // Milestone Achievements
  days_abroad_100: {
    unlocked: 'https://images.unsplash.com/photo-1496564203459-88bb61ec46bb?w=200&h=200&fit=crop&auto=format',
  },
  days_abroad_365: {
    unlocked: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=200&h=200&fit=crop&auto=format',
  },
  miles_traveled_10000: {
    unlocked: 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=200&h=200&fit=crop&auto=format',
  },
};

/**
 * Get image source for an achievement
 */
export function getAchievementImage(
  achievementId: string,
  isUnlocked: boolean
): string | null {
  const imageSource = ACHIEVEMENT_IMAGES[achievementId];
  if (!imageSource) return null;
  
  if (isUnlocked) {
    return imageSource.unlocked;
  }
  
  // For locked achievements, use locked image if available, otherwise use unlocked with opacity
  return imageSource.locked || imageSource.unlocked;
}

/**
 * Check if an achievement has an image
 */
export function hasAchievementImage(achievementId: string): boolean {
  return achievementId in ACHIEVEMENT_IMAGES;
}

